import clsx from "clsx";
import { useId } from "react";

import { motion } from "../components/presence";
import type { Lang } from "./i18n";

/** Drapeau de la langue de l'interface (demande d'Alexis du 24/10/2026 : « en fonction de la langue choisie, un
 *  petit drapeau correctement placé ») : drapeau français pour le français, Union Jack pour l'anglais (anglais
 *  britannique OTAN). Dessin SVG intégré — jamais d'emoji, absents des polices de Windows —, 14 × 10 px par défaut,
 *  coins de 2 px, liseré fin (`ring-line-strong`) pour que le clair ne se perde pas sur la surface. Teintes en
 *  jetons (`--color-flag-*`, index.css), en nuances de gris depuis le 26/10/2026 (« trop visibles ») : le dessin
 *  distingue les drapeaux sans attirer l'œil.
 *
 *  Décoratif par défaut (`aria-hidden`) : le bouton ou l'entrée qui le porte dit déjà la langue. `label` en fait une
 *  image nommée (« Français », « English ») quand le drapeau est seul. */
export function LangFlag({
  lang,
  size = 14,
  label,
  className,
  dimmed = false,
}: {
  lang: Lang;
  /** Largeur en pixels ; la hauteur suit (rapport 7 : 5). */
  size?: number;
  label?: string;
  className?: string;
  /** Langue active mise en avant : les autres drapeaux passent en retrait (estompés), en fondu. */
  dimmed?: boolean;
}) {
  const height = Math.round((size * 5) / 7);
  return (
    <span
      className={clsx(
        // Apparition en douceur (zoom) à l'affichage, donc à chaque changement de langue (l'application se remonte).
        "inline-flex shrink-0 overflow-hidden rounded-xs ring-1 ring-line-strong transition-[opacity,filter] duration-(--gx-enter)",
        motion("zoom"),
        dimmed && "opacity-55 saturate-50",
        className,
      )}
      style={{ width: size, height }}
      data-lang-flag={lang}
      {...(label ? { role: "img", "aria-label": label, title: label } : { "aria-hidden": true })}
    >
      {lang === "fr" ? <France /> : <UnionJack />}
    </span>
  );
}

function France() {
  return (
    <svg viewBox="0 0 3 2" width="100%" height="100%" preserveAspectRatio="none" aria-hidden focusable="false">
      <rect width="1" height="2" fill="var(--color-flag-fr-blue)" />
      <rect x="1" width="1" height="2" fill="var(--color-flag-white)" />
      <rect x="2" width="1" height="2" fill="var(--color-flag-fr-red)" />
    </svg>
  );
}

/** Union Jack (60 × 30), recadré au centre au rapport 7 : 5 comme les drapeaux d'icônes ; sautoirs rouges décalés
 *  (contre-changés) par un découpage en quartiers, comme le drapeau réel. */
function UnionJack() {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 60 30" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden focusable="false">
      <clipPath id={`${id}-quarters`}>
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <rect width="60" height="30" fill="var(--color-flag-uk-blue)" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="var(--color-flag-white)" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath={`url(#${id}-quarters)`} stroke="var(--color-flag-uk-red)" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="var(--color-flag-white)" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="var(--color-flag-uk-red)" strokeWidth="6" />
    </svg>
  );
}
